import { Attribute, Relationship, Resource } from 'json-api';
import { AtomResource } from '../classes/atom-resource';
import { Range } from '../classes/range';
import { CascadeLevel } from './cascade-level.resource';
import { Country } from './country.resource';
import { Enum } from './enum.resource';
import { FeeHistory, FeeHistoryConfig } from './fee-history.resource';
import { Firm } from './firm.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import { AtomService } from '../atom.service';
import {
  FirmConfig,
  CountryConfig,
} from 'src/app/shared/tools/search/search-config';
import {
  EnumListFilter,
  NumberFilter,
  StringFilter,
} from 'src/app/filter/filter';

@Resource({
  type: 'fees',
})
export class Fee extends AtomResource {
  @Attribute() name?: string;
  @Attribute() category?: 'vat' | 'wht' | 'revshare' | 'gm' | 'states_taxes';
  @Attribute() strategy?:
    | 'flat'
    | 'volume_flat'
    | 'volume_transaction'
    | 'tiered_flat'
    | 'tiered_transaction'
    | 'transaction';
  @Attribute() included?: boolean;

  @Relationship() country?: Country;
  @Relationship() firm?: Firm;
  @Relationship() enumcategory?: Enum;
  @Relationship() enumstrategy?: Enum;
  @Relationship() cascadelevels?: CascadeLevel[];
  @Relationship() feehistories?: FeeHistory[];

  @Attribute({
    convert: (value: string) => new Range<number>(value),
    revert: (value: Range<number>) => value.toString(),
  })
  range?: Range<number>;
}

export class FeeConfig implements ResourceConfig {
  tables = {
    includes: [
      'firm.headquarterscountry',
      'country',
      'enumcategory',
      'enumstrategy',
      // 'cascadelevels',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Name',
        displayed: true,
        sort: 'name',
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'category',
        displayedDatas: [
          {
            path: ['enumcategory.label'],
            class: 'mat-body-2',
          },
        ],
        header: 'Category',
        displayed: true,
        sort: 'cateogry',
      },
      {
        columnDef: 'strategy',
        displayedDatas: [
          {
            path: ['enumstrategy.label'],
            class: 'mat-body-2',
          },
        ],
        header: 'Strategy',
        displayed: true,
        sort: 'strategy',
      },
      {
        columnDef: 'included',
        displayedDatas: [
          {
            path: ['included'],
            class: 'mat-body-2',
            type: 'boolean',
          },
        ],
        header: 'Included ?',
        displayed: true,
        sort: 'included',
      },
      {
        columnDef: 'range',
        displayedDatas: [
          {
            path: ['range.min'],
            class: 'mat-body-2',
          },
          {
            path: ['range.max'],
            class: 'mat-body-2',
          },
        ],
        header: 'Range (min / max)',
        displayed: true,
        sort: 'range',
      },
      {
        columnDef: 'firm',
        displayedDatas: [
          {
            path: ['firm.name'],
            class: 'mat-body-2',
          },
          {
            path: ['firm.headquarterscountry.id'],
            class: 'mat-body-1',
          },
        ],
        header: 'Firm',
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'firm.id',
          icon: 'business',
        },
      },
      {
        columnDef: 'country',
        displayedDatas: [
          {
            path: ['country.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['country.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'country.id',
        },
        sort: 'country.name',
      },
      {
        columnDef: 'cascadelevels',
        header: 'Cascades',
        displayed: true,
        link: {
          resource: 'cascades',
          list: 'cascades',
          target: 'cascadelevels.fee',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'data_usage',
      title: [
        {
          path: ['name'],
          translatable: true,
        },
      ],
      id: true,
    },
    elements: [
      {
        showEmpty: true,
        columns: true,
        list: [
          {
            icon: 'category',
            title: 'Category',
            APIKey: 'enumcategory',
            value: {
              path: ['enumcategory.label'],
            },
          },
          {
            icon: 'settings',
            title: 'Strategy',
            APIKey: 'enumstrategy',
            value: {
              path: ['enumstrategy.label'],
            },
          },
          {
            icon: 'attach_money',
            title: 'Included ?',
            APIKey: 'included',
            value: {
              path: ['included'],
              type: 'boolean',
            },
          },
          {
            title: 'Country',
            APIKey: 'country',
            value: {
              path: ['country.name'],
            },
            link: {
              resource: 'countries',
              filter: 'country.id',
            },
            options: {
              flag: 'country.id',
            },
          },
          {
            title: 'Firm',
            APIKey: 'firm',
            icon: 'business',
            value: {
              path: ['firm.name'],
            },
            link: {
              resource: 'firms',
              filter: 'firm.id',
            },
          },
          {
            icon: 'code',
            title: 'Range',
            APIKey: 'range',
            value: {
              path: ['range.min', 'range.max'],
            },
          },
        ],
      },
      {
        title: 'Fee History',
        list: [
          {
            APIKey: 'feehistories',
            options: {
              sort: {
                type: 'date',
                editable: new FeeHistoryConfig(),
                displayedDatas: [
                  {
                    columnDef: 'rate',
                    title: 'Rate',
                    path: ['rate'],
                    nullIfEmpty: true,
                    zeroIsNull: true,
                    addUnit: {
                      fix: '%',
                    },
                  },
                  {
                    columnDef: 'amount',
                    title: 'Amount',
                    path: ['amount'],
                    nullIfEmpty: true,
                    zeroIsNull: true,
                    addUnit: {
                      path: 'fee.country.defaultcurrency.id',
                    },
                  },
                ],
              },
            },
          },
        ],
      },
    ],
  };
  wizard = {
    noSummary: true,
    tabs: [
      {
        title: 'General Informations',
        formGroup: 'general',
        list: [
          {
            type: 'text',
            title: 'Label',
            APIKey: 'name',
            formControl: 'name',
            required: true,
          },
          {
            type: 'enum',
            title: 'Category',
            APIKey: 'category',
            fieldKey: 'category',
            formControl: 'category',
            multiple: false,
            required: true,
          },
          {
            type: 'boolean',
            title: 'Included',
            APIKey: 'included',
            formControl: 'included',
            required: true,
            noCols: true,
          },
          {
            type: 'fee',
            title: 'Strategy & Value',
            APIKey: 'strategy',
            formControl: 'strategy',
            required: true,
            noCols: true,
          },
          {
            type: 'range',
            title: 'Range',
            APIKey: 'range',
            formControl: 'range',
            required: false,
            noCols: true,
          },
        ],
      },
      {
        title: 'Optional Informations',
        formGroup: 'optional',
        list: [
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            APIKey: 'country',
            search: new CountryConfig(this.atom),
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Firm',
            formControl: 'firm',
            APIKey: 'firm',
            search: new FirmConfig(this.atom),
            noCols: true,
          },
        ],
      },
    ],
  };
  includes = [
    'firm.headquarterscountry',
    'country',
    'enumcategory',
    'enumstrategy',
    'feehistories.fee.country.defaultcurrency',
  ];
  resource = Fee;
  resourceKey?: string | undefined = 'fees';
  resourceKeySingle?: string | undefined = 'fee';
  resourceName?: string | undefined = 'fees';
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name', true),
    new EnumListFilter(this.atom, 'fees', 'category', 'category', 'Category'),
    new EnumListFilter(this.atom, 'fees', 'strategy', 'strategy', 'Strategy'),
    new StringFilter('country.id', 'Country ID'),
    new StringFilter('country.name', 'Country name'),
    new StringFilter('firm.name', 'Firm name'),
    new NumberFilter('cascadelevels.cascade.id', 'Cascade ID'),
    new StringFilter('cascadelevels.cascade.name', 'Cascade name'),
  ];
  constructor(private atom: AtomService) {}
}
